import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../site/site.css';
import SiteNav from '../site/SiteNav';
import SiteFooter from '../site/SiteFooter';
import FieldBackground from '../site/FieldBackground';

function Home() {
  const fieldRef = useRef(null);
  const whatRef = useRef(null);
  const methodRef = useRef(null);
  const howRef = useRef(null);
  const [code, setCode] = useState(null);

  return (
    <div className="qs">
      <SiteNav active="home" />
      <main className="qs-field ruled" ref={fieldRef}>
        <FieldBackground
          containerRef={fieldRef}
          obstacles={[whatRef, methodRef, howRef]}
          onComplete={() => setCode(Math.random().toString(36).slice(2, 8).toUpperCase())}
        />
        <div className="qs-stage">
          <header className="qs-title">
            <div className="kicker">Quantum system simulator</div>
            <h1>Simulating quantum systems with <em>non-local hidden variables.</em></h1>
            <p>Qutron is a research simulator. Instead of storing a wavefunction, it models qubits as classical units whose correlations come from timing.</p>
          </header>

          <section className="qs-note qs-note--what" ref={whatRef}>
            <div className="kicker">01 · What it is</div>
            <h2>A quantum simulator</h2>
            <p>Build and run quantum experiments — qubits, entanglement, measurement — from a web-based lab with its own small language, Q.</p>
          </section>

          <section className="qs-note qs-note--method" ref={methodRef}>
            <div className="kicker">02 · A different method</div>
            <h2>No state vector</h2>
            <p>Conventional simulators store every amplitude of the system — a list that doubles with each qubit you add. Qutron takes another route: each qubit is a classical unit, a <em>qubyte</em>, and the correlations between them come from non-local hidden variables.</p>
          </section>

          <section className="qs-note qs-note--how" ref={howRef}>
            <div className="kicker">03 · How it works</div>
            <h2>Rotate, race, read out</h2>
            <div className="qs-steps">
              <div className="qs-step">
                <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true"><circle cx="28" cy="28" r="22" fill="none" stroke="#141A26" /><line x1="28" y1="28" x2="44" y2="14" stroke="#2A55C9" strokeWidth="2.5" /><circle cx="44" cy="14" r="3" fill="#2A55C9" /></svg>
                <p>A measurement setting turns each qubyte’s phase.</p>
              </div>
              <div className="qs-step">
                <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true"><line x1="4" y1="14" x2="52" y2="14" stroke="#141A26" /><line x1="4" y1="42" x2="52" y2="42" stroke="#141A26" /><line x1="12" y1="14" x2="34" y2="42" stroke="#2A55C9" strokeWidth="2.5" /><line x1="12" y1="42" x2="44" y2="14" stroke="#141A26" strokeDasharray="3 3" /></svg>
                <p>Paired qubytes signal each other at once. Arrival order is the hidden variable.</p>
              </div>
              <div className="qs-step">
                <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true"><rect x="6" y="10" width="18" height="36" fill="none" stroke="#141A26" /><rect x="32" y="10" width="18" height="36" fill="#2A55C9" fillOpacity="0.15" stroke="#141A26" /><text x="15" y="33" fontFamily="IBM Plex Mono, monospace" fontSize="11" textAnchor="middle" fill="#141A26">±1</text><text x="41" y="33" fontFamily="IBM Plex Mono, monospace" fontSize="11" textAnchor="middle" fill="#141A26">±1</text></svg>
                <p>Each side reads ±1. The statistics across runs are the result.</p>
              </div>
            </div>
            <div className="qs-note__links">
              <Link to="/About">More on the method →</Link>
              <a href="https://lab.qutron.co">Try it in the lab →</a>
            </div>
          </section>
        </div>

        {code && (
          <div className="qs-egg" role="status">
            You gathered every particle.<br />Code <span>{code}</span> — a free lab subscription.
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

export default Home;
